import type { LabelledDataSet } from "@/components/types";
import { getProducedTdhDataPoint } from "../tornado/tdh/getProducedDataSetsTdh";
import { getBhpCalibrationMultiplier, getRequiredBhpDataPoint } from "../tornado/bhp/getRequiredDataSetsBhp";
import { normalizeTornadoPoints } from "./normalizeTornadoPoints";
import { getDensityCorrectedProducedPoints } from "./densityCorrection";
import type { PredictiveCalcs, PumpTornadoChartContext } from "..";
import type { NumericPoint } from "@incytestudios/ddv";

type RorDataSetType = "min" | "max" | "bep";
export type AnalysisDataSetType = "tdh" | "bhp";

const getRorThresholdPoints = (
  analysisDataSetType: AnalysisDataSetType,
  rorThreshold: number | null | undefined,
  context: PumpTornadoChartContext
): NumericPoint[] => {
  const {
    pump,
    gasHeadRerateRatio,
    hypotheticalPumpFrequenciesHz,
    pumpWearHeadCorrectionRatio,
    pumpWearBHPCorrectionRatio,
    fluidDensitySpGr,
    dischargeFluidDensityLBMFT3,
    pumpRequiredBrakeHP,
    isScenarioEspShore,
    pumpDischargeFlowRateBBLD,
    pumpHz,
    ascendingOrderPumps
  } = context;

  const realFrequencyRatio =
    context.hypotheticalPumpFrequenciesIncludeRealHz.find(
      hf => hf.frequency === (pumpHz || 0)
    )?.frequencyRatio || 1;

  const dataSets = hypotheticalPumpFrequenciesHz.map(hf => {
    const { frequencyRatio } = hf;
    const rorX =
      (rorThreshold || 0) * pump.rate_correction_ratio * frequencyRatio;
    const params = {
      flowRateBblD: rorX,
      frequencyRatio,
      ascendingOrderPumps,
      gasHeadRerateRatio,
      pumpWearHeadCorrectionRatio,
      pumpWearBHPCorrectionRatio,
      fluidDensitySpGr,
      dischargeFluidDensityLBMFT3,
      pumpRequiredBrakeHP,
      isScenarioEspShore,
      bhpCalibrationMultiplier: getBhpCalibrationMultiplier({
        frequencyRatio,
        realFrequencyRatio,
        pumpDischargeFlowRateBBLD,
        pumpRequiredBrakeHP,
        ascendingOrderPumps,
        fluidDensitySpGr,
        pumpWearBHPCorrectionRatio
      })
    };

    let pt;
    switch (analysisDataSetType) {
      case "bhp":
        pt = getRequiredBhpDataPoint(params);
        break;
      case "tdh":
        pt = getProducedTdhDataPoint(params);
        break;
    }

    return {
      ...pt,
      y: pt.y
    };
  });

  return dataSets;
};

const getRorDataSet = (
  context: PumpTornadoChartContext,
  rorDataSetType: RorDataSetType,
  analysisDataSetType: AnalysisDataSetType,
  predictedCalcsWithDensityCorrectionRatio?: PredictiveCalcs
): LabelledDataSet => {
  if (!context) {
    return {
      label: "",
      points: []
    };
  }
  const {
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseTdhYUnit,
    targetTdhYUnit,
    targetTdhYUnitLabel,
    baseBhpYUnit,
    targetBhpYUnit,
    targetBhpYUnitLabel,
    rorMin,
    rorMax,
    rorBep,
    pumpHz
  } = context;

  let rorThreshold;
  let title;
  let label = "";
  let color = "var(--gray-600);";

  if (rorDataSetType === "min") {
    rorThreshold = rorMin;
    title = "ROR MIN";
    label = "ROR MIN";
  } else if (rorDataSetType === "max") {
    rorThreshold = rorMax;
    title = "ROR MAX";
    label = "ROR MAX";
  } else if (rorDataSetType === "bep") {
    rorThreshold = rorBep;
    title = "BEP";
    label = "BEP";
    color = "var(--rorBepColor);";
  }

  const yAxisLabel =
    analysisDataSetType === "tdh" ? targetTdhYUnitLabel : targetBhpYUnitLabel;
  const rorThresholdPoints = getRorThresholdPoints(
    analysisDataSetType,
    rorThreshold,
    context
  );
  const correctedRorThresholdPoints =
    analysisDataSetType === "tdh"
      ? rorThresholdPoints.map(point => {
          const densityCorrectionRatios =
            predictedCalcsWithDensityCorrectionRatio?.[pumpHz]
              ?.densityCorrectionRatios;
          // Match produced TDH dataset correction basis (ESP uses TDH, HPS uses pressure).
          const densityCorrectionRatio = context.isScenarioEspShore
            ? densityCorrectionRatios?.densityCorrectionTDHRatio
            : densityCorrectionRatios?.densityCorrectionPressureRatio;
          const [correctedPoint] = getDensityCorrectedProducedPoints(
            [point],
            densityCorrectionRatio
          );
          return correctedPoint;
        })
      : rorThresholdPoints;
  const rorBaseYUnit =
    analysisDataSetType === "tdh" ? baseTdhYUnit : baseBhpYUnit;
  const rorTargetYUnit =
    analysisDataSetType === "tdh" ? targetTdhYUnit : targetBhpYUnit;
  return {
    title,
    color,
    xAxisLabel: targetXUnitLabel,
    yAxisLabel,
    points: normalizeTornadoPoints(
      correctedRorThresholdPoints,
      baseXUnit,
      targetXUnit,
      rorBaseYUnit,
      rorTargetYUnit
    ),
    label
  };
};

export const getRorDataSets = (
  context: PumpTornadoChartContext,
  analysisDataSetType: AnalysisDataSetType,
  predictedCalcsWithDensityCorrectionRatio?: PredictiveCalcs
): LabelledDataSet[] => {
  return [
    getRorDataSet(
      context,
      "min",
      analysisDataSetType,
      predictedCalcsWithDensityCorrectionRatio
    ),
    getRorDataSet(
      context,
      "max",
      analysisDataSetType,
      predictedCalcsWithDensityCorrectionRatio
    ),
    getRorDataSet(
      context,
      "bep",
      analysisDataSetType,
      predictedCalcsWithDensityCorrectionRatio
    )
  ];
};
